import React from "react";
import LegalLayout from "@/components/LegalLayout";
import { Link } from "react-router-dom";
import {
  Envelope,
  Question,
  ChatCircleText,
  Lifebuoy,
  Lock,
  CreditCard,
  Shield,
  Bug,
  ArrowRight,
  Clock,
} from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";

/**
 * Support page — primary help hub linking out to mail, FAQ, contact form,
 * plus condensed how-to blocks for the most common requests.
 */
export default function Support() {
  const { t, lang } = useI18n();
  return (
    <LegalLayout
      kicker={t("legal.support_kicker")}
      title={t("legal.support_title")}
      lastUpdated={lang === "da" ? "Maj 2026" : "May 2026"}
    >
      <p>
        {lang === "da"
          ? "Vi er et lille team og bestræber os på at svare inden for 1–2 hverdage. Tjek FAQ først — der finder du svar på de fleste spørgsmål med det samme."
          : "We're a small team and aim to reply within 1–2 business days. Check the FAQ first — most questions are answered there instantly."}
      </p>

      <div className="not-prose grid sm:grid-cols-3 gap-3 mt-6 mb-10">
        <SupportTile
          icon={<Envelope weight="duotone" size={22} color="#007AFF" />}
          label={lang === "da" ? "Skriv til os" : "Email us"}
          to="mailto:support@teamfit.dk"
          value="support@teamfit.dk"
          testId="support-email"
        />
        <SupportTile
          icon={<Question weight="duotone" size={22} color="#FF3B30" />}
          label="FAQ"
          to="/faq"
          value={lang === "da" ? "Ofte stillede spørgsmål" : "Frequently asked"}
          testId="support-faq"
        />
        <SupportTile
          icon={<ChatCircleText weight="duotone" size={22} color="#34C759" />}
          label={lang === "da" ? "Kontakt" : "Contact"}
          to="/contact"
          value={lang === "da" ? "Send en besked" : "Send a message"}
          testId="support-contact"
        />
      </div>

      <div className="not-prose tf-card p-5 flex items-center gap-4 mb-12" data-testid="support-response-time">
        <div className="w-10 h-10 flex items-center justify-center border" style={{ borderColor: "var(--tf-border)" }}>
          <Clock weight="duotone" size={20} color="#34C759" />
        </div>
        <div>
          <div className="label-overline">{lang === "da" ? "Svartid" : "Response time"}</div>
          <div className="font-display text-lg tracking-tight">
            {lang === "da" ? "1–2 hverdage" : "1–2 business days"}
          </div>
          <div className="text-xs text-zinc-500 mt-1 font-mono-tag">
            {lang === "da" ? "Man–fre · 09:00–17:00 CET" : "Mon–Fri · 9:00 AM–5:00 PM CET"}
          </div>
        </div>
      </div>

      <h2>{lang === "da" ? "Hvad kan vi hjælpe med" : "What we can help with"}</h2>

      <HelpBlock
        icon={<Lock weight="duotone" size={22} color="#007AFF" />}
        title={lang === "da" ? "Problemer med login" : "Trouble signing in"}
        body={
          lang === "da"
            ? <>Tjek først at din e-mail er korrekt skrevet. Bruger du Apple eller Google? Sørg for at du logger ind med samme metode som ved oprettelsen. Glemt password? Tryk på <i>"Glemt password"</i> på loginsiden, eller skriv til os.</>
            : <>First make sure your email is typed correctly. Did you use Apple or Google to sign up? You'll need to use the same method to sign back in. Forgot your password? Tap <i>"Forgot password"</i> on the login screen, or email us.</>
        }
      />

      <HelpBlock
        icon={<CreditCard weight="duotone" size={22} color="#FFCC00" />}
        title={lang === "da" ? "Pro Crew & fakturering" : "Pro Crew & billing"}
        body={
          lang === "da"
            ? <>Køb og annullering af Pro håndteres via Apple App Store eller Google Play. For at annullere: gå til din enheds <b>Indstillinger → Apple-ID / Google → Abonnementer → TeamFit</b>. Du beholder Pro indtil periodens udløb. Spørgsmål om refundering? Skriv til <a href="mailto:support@teamfit.dk">support@teamfit.dk</a>.</>
            : <>Pro purchases and cancellations are handled by Apple App Store or Google Play. To cancel: go to your device <b>Settings → Apple ID / Google → Subscriptions → TeamFit</b>. You keep Pro until the period ends. Refund questions? Email <a href="mailto:support@teamfit.dk">support@teamfit.dk</a>.</>
        }
      />

      <HelpBlock
        icon={<Shield weight="duotone" size={22} color="#FF3B30" />}
        title={lang === "da" ? "Anmeld en bruger eller session" : "Report a user or session"}
        body={
          lang === "da"
            ? <>Hvis du oplever upassende adfærd, chikane eller en falsk profil, så skriv til <a href="mailto:support@teamfit.dk">support@teamfit.dk</a> med navn, sessions-ID eller link. Vi reagerer hurtigt og kan suspendere eller fjerne konti der bryder vores <Link to="/terms">brugervilkår</Link>.</>
            : <>If you experience inappropriate behavior, harassment or a fake profile, email <a href="mailto:support@teamfit.dk">support@teamfit.dk</a> with the name, activity ID or link. We respond fast and can suspend or remove accounts violating our <Link to="/terms">Terms of Service</Link>.</>
        }
      />

      <HelpBlock
        icon={<Bug weight="duotone" size={22} color="#34C759" />}
        title={lang === "da" ? "Fejl, nedbrud eller forslag" : "Bugs, crashes or feature requests"}
        body={
          lang === "da"
            ? <>Vi hører gerne fra dig. Beskriv hvad du forsøgte at gøre, hvad der skete, og hvilken enhed/version du bruger. Skærmbilleder hjælper rigtig meget. Send til <a href="mailto:support@teamfit.dk">support@teamfit.dk</a> eller via <Link to="/contact">kontaktformularen</Link>.</>
            : <>We love hearing from you. Describe what you tried to do, what happened, and which device/version you're on. Screenshots help a lot. Send to <a href="mailto:support@teamfit.dk">support@teamfit.dk</a> or via the <Link to="/contact">contact form</Link>.</>
        }
      />

      <HelpBlock
        icon={<Lifebuoy weight="duotone" size={22} color="#007AFF" />}
        title={lang === "da" ? "Slet din konto" : "Delete your account"}
        body={
          lang === "da"
            ? <>Du kan slette din konto direkte i appen under <b>Profil → Slet konto</b>. Personlige data fjernes eller anonymiseres inden for rimelig tid. Hvis du ikke kan logge ind, så skriv til os fra den e-mail du oprettede kontoen med, så sletter vi den manuelt.</>
            : <>You can delete your account directly in the app under <b>Profile → Delete account</b>. Personal data is removed or anonymized within a reasonable timeframe. If you can't sign in, email us from the address you signed up with and we'll delete it manually.</>
        }
      />

      <h2>{lang === "da" ? "I en nødsituation" : "In an emergency"}</h2>
      <p>
        {lang === "da"
          ? <>TeamFit er ikke en nødtjeneste. Ring <b>112</b> ved akut fare, skade eller ulykke under en session. Beskyttelse af din egen sikkerhed kommer altid først.</>
          : <>TeamFit is not an emergency service. Call <b>112</b> (EU) or <b>911</b> (US) for immediate danger, injury or accident during a session. Your safety always comes first.</>}
      </p>

      <h2>{lang === "da" ? "Stadig brug for hjælp?" : "Still need help?"}</h2>
      <p>
        {lang === "da"
          ? <>Skriv til <a href="mailto:support@teamfit.dk">support@teamfit.dk</a> — vi læser alle henvendelser.</>
          : <>Email <a href="mailto:support@teamfit.dk">support@teamfit.dk</a> — we read every message.</>}
      </p>
    </LegalLayout>
  );
}

function SupportTile({ icon, label, to, value, testId }) {
  const isExternal = to.startsWith("mailto:") || to.startsWith("http");
  const cls = "tf-card p-5 block hover:border-[#007AFF] transition-colors no-underline group";
  const inner = (
    <>
      <div className="w-10 h-10 flex items-center justify-center border" style={{ borderColor: "var(--tf-border)" }}>
        {icon}
      </div>
      <div className="label-overline mt-4">{label}</div>
      <div className="font-display text-lg mt-1 tracking-tight flex items-center gap-1">
        {value}
        <ArrowRight weight="bold" size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </>
  );
  return isExternal ? (
    <a href={to} className={cls} data-testid={testId}>{inner}</a>
  ) : (
    <Link to={to} className={cls} data-testid={testId}>{inner}</Link>
  );
}

function HelpBlock({ icon, title, body }) {
  return (
    <div className="not-prose tf-card p-5 mb-3 flex gap-4" data-testid={`help-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}>
      <div className="w-10 h-10 flex items-center justify-center border shrink-0" style={{ borderColor: "var(--tf-border)" }}>
        {icon}
      </div>
      <div className="min-w-0">
        <div className="font-display text-lg tracking-tight mb-1">{title}</div>
        <div className="text-sm leading-relaxed" style={{ color: "var(--tf-text-2)" }}>{body}</div>
      </div>
    </div>
  );
}
